/** @module Accounts and invites: who may use the app, and links that let someone new in. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Link2, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, ApiError, type components, problemFrom } from "@/api/client";
import { Segmented, Switch } from "@/components/ui/controls";
import { day } from "@/lib/format";
import { useSession } from "@/lib/use-session";

type User = components["schemas"]["UserOut"];
type Invite = components["schemas"]["InviteOut"];
type Role = "user" | "admin";

async function fetchUsers(): Promise<User[]> {
  const { data, response } = await api.GET("/api/v1/admin/users");
  if (!data) throw problemFrom(response);
  return data;
}

async function fetchInvites(): Promise<Invite[]> {
  const { data, response } = await api.GET("/api/v1/admin/invites");
  if (!data) throw problemFrom(response);
  return data;
}

/** The link a new person opens; the token is shown once, right after creating the invite. */
export function inviteLink(token: string, origin = window.location.origin): string {
  return `${origin}/register?invite=${encodeURIComponent(token)}`;
}

function UserRow({ user }: { user: User }) {
  const { t, i18n } = useTranslation();
  const client = useQueryClient();
  const session = useSession();
  const self = session.account?.id === user.id;
  const update = useMutation({
    mutationFn: async (body: { role?: Role; disabled?: boolean }) => {
      const { data, response } = await api.PATCH("/api/v1/admin/users/{user_id}", {
        params: { path: { user_id: user.id } },
        body,
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
  return (
    <tr>
      <td>
        <span className="flex flex-col">
          <b>{user.display_name}</b>
          <span className="t-xs t-muted">{user.email}</span>
        </span>
      </td>
      <td>
        {/* Nobody demotes or locks themselves out; the server refuses it as well. */}
        <Segmented
          label={t("admin.users.role")}
          value={user.role}
          onChange={(role) => {
            if (!self) update.mutate({ role });
          }}
          options={[
            { value: "user", label: t("admin.users.roleUser") },
            { value: "admin", label: t("admin.users.roleAdmin") },
          ]}
        />
      </td>
      <td className="num t-sm">{day(user.created_at, i18n.resolvedLanguage ?? "de")}</td>
      <td className="td-num">
        <span className="row justify-end gap-2">
          <span className="t-sm">
            {user.disabled_at ? t("admin.users.disabled") : t("admin.users.active")}
          </span>
          <Switch
            checked={!user.disabled_at}
            disabled={self || update.isPending}
            label={t("admin.users.enabledFor", { name: user.display_name })}
            onChange={(enabled) => {
              update.mutate({ disabled: !enabled });
            }}
          />
        </span>
        {update.isError ? (
          <span role="alert" className="t-xs loss block">
            {update.error instanceof ApiError ? update.error.message : t("admin.users.error")}
          </span>
        ) : null}
      </td>
    </tr>
  );
}

function NewInvite() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [role, setRole] = useState<Role>("user");
  const [note, setNote] = useState("");
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const create = useMutation({
    mutationFn: async () => {
      const { data, response } = await api.POST("/api/v1/admin/invites", {
        body: { role, note: note.trim() || null },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
    onSuccess: (invite) => {
      setLink(invite.token ? inviteLink(invite.token) : null);
      setCopied(false);
      setNote("");
      void client.invalidateQueries({ queryKey: ["admin", "invites"] });
    },
  });

  return (
    <div className="panel panel-lg flex flex-col gap-3">
      <h3 className="t-h3">{t("admin.users.newInvite")}</h3>
      <div className="flex flex-col gap-3 md:flex-row md:items-end">
        <label className="field grow">
          <span className="field-label">{t("admin.users.note")}</span>
          <input
            className="input"
            value={note}
            maxLength={200}
            placeholder={t("admin.users.notePlaceholder")}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </label>
        <Segmented
          label={t("admin.users.role")}
          value={role}
          onChange={setRole}
          options={[
            { value: "user", label: t("admin.users.roleUser") },
            { value: "admin", label: t("admin.users.roleAdmin") },
          ]}
        />
        <button
          type="button"
          className="btn btn-primary"
          disabled={create.isPending}
          onClick={() => {
            create.mutate();
          }}
        >
          <Link2 />
          {t("admin.users.create")}
        </button>
      </div>
      {link ? (
        <div className="callout callout-accent flex-col items-stretch">
          <span className="t-sm">{t("admin.users.linkOnce")}</span>
          <span className="row gap-2">
            <input
              className="input num grow text-[12px]"
              readOnly
              value={link}
              aria-label={t("admin.users.link")}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                void navigator.clipboard.writeText(link).then(() => {
                  setCopied(true);
                });
              }}
            >
              {copied ? <Check /> : <Copy />}
              {copied ? t("admin.users.copied") : t("admin.users.copy")}
            </button>
          </span>
        </div>
      ) : null}
      {create.isError ? (
        <p role="alert" className="t-sm loss">
          {t("admin.users.error")}
        </p>
      ) : null}
    </div>
  );
}

export function UsersPanel() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const client = useQueryClient();
  const users = useQuery({ queryKey: ["admin", "users"], queryFn: fetchUsers });
  const invites = useQuery({ queryKey: ["admin", "invites"], queryFn: fetchInvites });
  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const { response } = await api.DELETE("/api/v1/admin/invites/{invite_id}", {
        params: { path: { invite_id: id } },
      });
      if (!response.ok) throw problemFrom(response);
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin", "invites"] });
    },
  });
  const open = (invites.data ?? []).filter(
    (invite) => !invite.used_at && new Date(invite.expires_at) > new Date(),
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="panel flex flex-col overflow-x-auto">
        <h3 className="t-h3 px-4 pt-[14px]">{t("admin.users.accounts")}</h3>
        <table className="table">
          <thead>
            <tr>
              <th>{t("admin.users.name")}</th>
              <th>{t("admin.users.role")}</th>
              <th>{t("admin.users.since")}</th>
              <th className="td-num">{t("admin.users.status")}</th>
            </tr>
          </thead>
          <tbody>
            {(users.data ?? []).map((user) => (
              <UserRow key={user.id} user={user} />
            ))}
          </tbody>
        </table>
      </div>
      <NewInvite />
      <div className="panel panel-lg flex flex-col gap-2">
        <h3 className="t-h3">{t("admin.users.openInvites")}</h3>
        {open.length === 0 ? (
          <p className="t-sm t-muted">{t("admin.users.noInvites")}</p>
        ) : (
          <ul className="flex flex-col">
            {open.map((invite) => (
              <li
                key={invite.id}
                className="row-between min-h-12 border-b border-(--border) last:border-0"
              >
                <span className="flex flex-col">
                  <span>{invite.note ?? t("admin.users.noNote")}</span>
                  <span className="t-xs t-muted">
                    {invite.role === "admin"
                      ? t("admin.users.roleAdmin")
                      : t("admin.users.roleUser")}
                    {" · "}
                    {t("admin.users.until", { date: day(invite.expires_at, locale) })}
                  </span>
                </span>
                <button
                  type="button"
                  className="icon-btn"
                  aria-label={t("admin.users.revoke")}
                  disabled={revoke.isPending}
                  onClick={() => {
                    revoke.mutate(invite.id);
                  }}
                >
                  <Trash2 />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
